import { type Op } from 'quill-delta';

import { type GlobalStorageFile } from 'hb-react/shared/hooks/useGlobalStorageV2/types';

interface InlineImageOperation extends Op {
  id: string;
  src: string;
  style: string;
  width: string;
}

export const updateInlineImageBlotUrls = ({
  ops = [],
  urls = {},
}: {
  ops: Array<Op>;
  urls: object;
}) =>
  ops.reduce((newOps, op) => {
    if (typeof op.insert === 'string') {
      newOps.push(op);

      return newOps;
    }

    if (!op.insert?.['inline-image']) {
      newOps.push(op);

      return newOps;
    }

    const { id } = op.insert['inline-image'] as { id: string };
    const newUrl = urls[id];

    // Likely an old inline-image
    if (!newUrl) {
      newOps.push(op);

      return newOps;
    }

    const newOp = {
      ...op,
      insert: {
        ...op.insert,
        'inline-image': {
          ...op.insert['inline-image'],
          src: newUrl, // Update the img url
        },
      },
    };

    newOps.push(newOp);

    return newOps;
  }, [] as Array<Op>);

export const getInlineImageBlots = (ops: Array<Op> = []) =>
  ops.reduce((iiBlots, op) => {
    const iiBlot = op.insert?.['inline-image'];

    if (iiBlot) {
      iiBlots.push(iiBlot);
    }

    return iiBlots;
  }, [] as Array<InlineImageOperation>);

// Remove inline-image blot that match the provided ids from ops
export const removeInlineImageBlot = ({
  ops = [],
  keys = [],
}: {
  keys: Array<string>;
  ops: Array<Op>;
}) =>
  ops.reduce(
    (newOps, op) =>
      keys.includes(op.insert?.['inline-image']?.id) ? newOps : [...newOps, op],
    [] as Array<Op>,
  );

export const updateInlineImagesFiles = (
  prevFiles: Array<GlobalStorageFile>,
  ops: Array<Op>,
): Array<GlobalStorageFile> => {
  if (prevFiles.length === 0) {
    return prevFiles;
  }

  const iiBlots = getInlineImageBlots(ops);

  const contentImagesKeys = iiBlots.map(({ id }) => id);

  let modified = false;

  const newFiles = prevFiles.reduce(
    (newFiles: Array<GlobalStorageFile>, file) => {
      const isRemoved = !contentImagesKeys.includes(file.key);

      // Do NOT abort the transactions (file.abortController.abort()) when the file
      // is removed from the content because users can always UNDO and bring the image back.
      if (Boolean(file.isRemoved) !== Boolean(isRemoved)) {
        modified = true;

        newFiles.push({
          ...file,
          isRemoved,
        });

        return newFiles;
      }
      newFiles.push(file);

      return newFiles;
    },
    [] as Array<GlobalStorageFile>,
  );

  if (modified) {
    return newFiles;
  }

  return prevFiles;
};
